import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, WhatsappLogo, EnvelopeSimple, Copy, Check, ArrowsClockwise, Trash, MagnifyingGlass } from "@phosphor-icons/react";

const EMPTY_FORM = {
  player_id: "",
  player_name: "",
  player_dob: "",
  parent_name: "",
  parent_email: "",
  parent_phone: "",
};

export default function ClubInvites() {
  const [invites, setInvites] = useState(null);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [createdInvite, setCreatedInvite] = useState(null);
  const [instantLink, setInstantLink] = useState(null);
  const [phoneMatch, setPhoneMatch] = useState(null); // { data, payload }
  const [err, setErr] = useState("");

  const refresh = () => api.get("/invites").then((r) => setInvites(r.data));
  const refreshPlayers = () => api.get("/players").then((r) => setPlayers(r.data || []));
  useEffect(() => { refresh(); refreshPlayers(); }, []);

  const invitedPlayerIds = useMemo(() => {
    const ids = new Set();
    (invites || []).forEach((i) => {
      if (i.status === "pending" && i.player_id) ids.add(i.player_id);
    });
    return ids;
  }, [invites]);

  const availablePlayers = useMemo(() => {
    return (players || [])
      .filter((p) => p.active !== false)
      .map((p) => ({ ...p, _alreadyInvited: invitedPlayerIds.has(p.id) }));
  }, [players, invitedPlayerIds]);

  const filteredPlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availablePlayers;
    return availablePlayers.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [availablePlayers, search]);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setSearch("");
    setErr("");
    // Default to 'existing' — the empty-state inside the existing panel already
    // nudges the user to switch to 'new' if their roster is empty. This avoids
    // a race where openModal fires before /api/players resolves.
    setMode("existing");
    setOpen(true);
  };

  const pickPlayer = (p) => {
    setForm({
      player_id: p.id,
      player_name: p.name || "",
      player_dob: (p.date_of_birth || "").slice(0, 10),
      parent_name: p.parent_name || "",
      parent_email: p.email || "",
      parent_phone: p.phone || "",
    });
    setErr("");
  };

  const clearPick = () => setForm({ ...EMPTY_FORM });

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.parent_email && !form.parent_phone) {
      setErr("Add an email or a phone number so the invite can reach the parent.");
      return;
    }
    try {
      const payload = mode === "existing"
        ? {
            player_id: form.player_id,
            parent_name: form.parent_name,
            parent_email: form.parent_email || undefined,
            parent_phone: form.parent_phone || undefined,
          }
        : {
            player_name: form.player_name,
            player_dob: form.player_dob,
            parent_name: form.parent_name,
            parent_email: form.parent_email || undefined,
            parent_phone: form.parent_phone || undefined,
          };
      const res = await api.post("/invites", payload);
      if (res.data.phone_match) {
        // Same phone as an existing parent — ask the admin before linking.
        setPhoneMatch({
          data: res.data,
          payload: {
            player_id: res.data.player_id,
            parent_name: form.parent_name,
            parent_email: form.parent_email || undefined,
            parent_phone: form.parent_phone || undefined,
          },
        });
      } else if (res.data.instant_link) {
        setInstantLink(res.data);
      } else {
        setCreatedInvite(res.data);
      }
      setForm(EMPTY_FORM);
      setSearch("");
      setOpen(false);
      refresh();
      refreshPlayers();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const cancel = async (id) => {
    await api.delete(`/invites/${id}`);
    refresh();
  };

  const resend = async (inv) => {
    const res = await api.post(`/invites/${inv.id}/resend`);
    refresh();
    setCreatedInvite({ ...inv, token: res.data.token });
  };

  return (
    <div className="space-y-5" data-testid="club-invites">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label">Onboarding</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Parent invites</h1>
          <p className="text-zinc-400 text-sm mt-1">Pick an existing player from your roster (faster) or add a new one, then share the activation link.</p>
        </div>
        <button className="btn-primary" onClick={openModal} data-testid="new-invite-btn">
          <Plus size={16} weight="bold" /> New invite
        </button>
      </div>

      {!invites && <div className="skeleton h-32" />}
      {invites?.length === 0 && <div className="surface p-6 text-zinc-400 text-sm">No invites yet. Create one to onboard a parent.</div>}

      <div className="grid gap-2">
        {invites?.map((i) => (
          <div key={i.id} className="surface p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`invite-row-${i.id}`}>
            <div className="min-w-0">
              <div className="font-semibold">{i.player_name}</div>
              <div className="text-xs text-zinc-500">
                Parent: {i.parent_name} • {i.parent_email || "—"} {i.parent_phone ? ` • ${i.parent_phone}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs px-2 py-0.5 rounded uppercase tracking-wider font-bold ${
                  i.status === "pending" ? "bg-[#dfff00]/10 text-[#dfff00]" :
                  i.status === "accepted" ? "bg-[#16ec06]/10 text-[#16ec06]" :
                  "bg-zinc-700 text-zinc-300"
                }`}
              >
                {i.status}
              </span>
              {i.status === "pending" && (
                <>
                  <button onClick={() => setCreatedInvite(i)} className="btn-ghost text-xs" data-testid={`share-invite-${i.id}`}>
                    Share
                  </button>
                  <button onClick={() => resend(i)} className="btn-ghost text-xs" data-testid={`resend-invite-${i.id}`} title="Regenerate link">
                    <ArrowsClockwise size={14} weight="bold" />
                  </button>
                  <button onClick={() => cancel(i.id)} className="btn-ghost text-[#ff0026]" data-testid={`cancel-invite-${i.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* New invite modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="surface p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-invite-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase text-xl">New invite</h2>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X size={18} /></button>
            </div>

            {/* Mode toggle */}
            <div className="inline-flex rounded-md border border-[#27272a] overflow-hidden mb-4">
              <button
                type="button"
                onClick={() => { setMode("existing"); clearPick(); }}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold ${mode === "existing" ? "bg-[#dfff00] text-black" : "text-zinc-300 hover:bg-zinc-800"}`}
                data-testid="invite-mode-existing"
              >
                Existing player
              </button>
              <button
                type="button"
                onClick={() => { setMode("new"); clearPick(); }}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold ${mode === "new" ? "bg-[#dfff00] text-black" : "text-zinc-300 hover:bg-zinc-800"}`}
                data-testid="invite-mode-new"
              >
                New player
              </button>
            </div>

            {mode === "existing" ? (
              <div className="space-y-3">
                {!form.player_id ? (
                  <>
                    <label className="font-label block mb-1">Search your roster</label>
                    <div className="relative">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none z-10" />
                      <input
                        className="input"
                        style={{ paddingLeft: "2.5rem" }}
                        placeholder="Type a player name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="invite-player-search"
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto border border-[#27272a] rounded-md divide-y divide-[#27272a]" data-testid="invite-player-list">
                      {filteredPlayers.length === 0 && (
                        <div className="p-3 text-xs text-zinc-500">No players match. Switch to &quot;New player&quot; to add one.</div>
                      )}
                      {filteredPlayers.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => !p._alreadyInvited && pickPlayer(p)}
                          disabled={p._alreadyInvited}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${p._alreadyInvited ? "opacity-50 cursor-not-allowed" : "hover:bg-zinc-800"}`}
                          data-testid={`invite-player-option-${p.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{p.name}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {p.email || "no email"} {p.phone ? `• ${p.phone}` : ""}
                            </div>
                          </div>
                          {p._alreadyInvited && (
                            <span className="text-[10px] uppercase tracking-wider bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">Invited</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="bg-[#09090b] border border-[#dfff00]/40 rounded-md p-3 flex items-center justify-between" data-testid="invite-player-picked">
                    <div className="min-w-0">
                      <div className="font-label">Selected player</div>
                      <div className="text-sm font-semibold truncate">{form.player_name}</div>
                      {form.player_dob && <div className="text-[10px] text-zinc-500 font-mono">DOB {form.player_dob}</div>}
                    </div>
                    <button type="button" onClick={clearPick} className="btn-ghost text-xs" data-testid="invite-player-clear">Change</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="font-label block mb-1">Player name</label>
                  <input required className="input" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} data-testid="invite-player-name" />
                </div>
                <div>
                  <label className="font-label block mb-1">Player date of birth</label>
                  <input required type="date" className="input" value={form.player_dob} onChange={(e) => setForm({ ...form, player_dob: e.target.value })} data-testid="invite-player-dob" />
                </div>
              </div>
            )}

            {/* Parent details (shared) */}
            {(mode === "new" || form.player_id) && (
              <div className="space-y-3 mt-4 pt-4 border-t border-[#27272a]">
                <div>
                  <label className="font-label block mb-1">Parent name</label>
                  <input required className="input" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="invite-parent-name" />
                </div>
                <div>
                  <label className="font-label block mb-1">Parent email (or use phone below)</label>
                  <input type="email" className="input" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value.replace(/\s+/g, "") })} data-testid="invite-parent-email" />
                  {mode === "existing" && form.parent_email && (
                    <div className="text-[10px] text-zinc-500 mt-1">Pre-filled from player record — edit if needed.</div>
                  )}
                </div>
                <div>
                  <label className="font-label block mb-1">Parent phone (optional, for WhatsApp)</label>
                  <input className="input" placeholder="+447700900111" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} data-testid="invite-parent-phone" />
                </div>
                {err && <div className="text-sm text-[#ff0026]">{err}</div>}
                <button type="submit" className="btn-primary w-full" data-testid="invite-submit">Create invite</button>
              </div>
            )}
          </form>
        </div>
      )}

      {createdInvite && (
        <ShareModal invite={createdInvite} onClose={() => setCreatedInvite(null)} />
      )}

      {phoneMatch && (
        <PhoneMatchModal
          info={phoneMatch}
          onClose={() => setPhoneMatch(null)}
          onLinked={(d) => { setPhoneMatch(null); setInstantLink(d); refresh(); }}
          onInvited={(inv) => { setPhoneMatch(null); setCreatedInvite(inv); refresh(); }}
        />
      )}

      {instantLink && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setInstantLink(null)}>
          <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="instant-link-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#16ec06]/10 flex items-center justify-center shrink-0">
                <Check size={20} weight="bold" className="text-[#16ec06]" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading uppercase text-xl">
                  {instantLink.already_linked ? "Already linked" : "Linked instantly"}
                </h2>
                <p className="text-sm text-zinc-400 mt-1" data-testid="instant-link-text">
                  {instantLink.parent_email} already has a parent account
                  {instantLink.already_linked
                    ? ` — ${instantLink.player_name} was already on it. No invite needed.`
                    : `, so ${instantLink.player_name} was added to it directly — no activation link needed.`}
                  {instantLink.email_sent
                    ? ` ${instantLink.parent_name || "They"} got an in-app notification and an email.`
                    : ` ${instantLink.parent_name || "They"} got an in-app notification.`}
                </p>
              </div>
            </div>
            <button onClick={() => setInstantLink(null)} className="btn-primary w-full mt-5" data-testid="instant-link-done">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function normalisePhone(raw) {
  // WhatsApp's wa.me requires international format, digits only, NO leading +, NO leading 0.
  // We accept inputs like "+44 7901 123456", "07901 123456", "(44) 7901-123456" and normalise.
  if (!raw) return "";
  let s = String(raw).trim();
  // Strip everything but + and digits first
  s = s.replace(/[^\d+]/g, "");
  // If already in +CC… form, drop the +
  if (s.startsWith("+")) return s.slice(1);
  // If begins with 00 (international prefix), drop it
  if (s.startsWith("00")) return s.slice(2);
  // Pure UK national leading-0 → assume UK (44)
  if (s.startsWith("0")) return "44" + s.slice(1);
  // Otherwise assume already in international form (e.g. "447901…")
  return s;
}

function PhoneMatchModal({ info, onClose, onLinked, onInvited }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const { data, payload } = info;

  const linkIt = async () => {
    setBusy("link"); setErr("");
    try {
      const res = await api.post("/invites", { ...payload, link_to_user_id: data.match.user_id });
      onLinked(res.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const separateInvite = async () => {
    setBusy("invite"); setErr("");
    try {
      const res = await api.post("/invites", { ...payload, force_invite: true });
      onInvited(res.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="phone-match-modal">
        <div className="font-label text-[#dfff00] mb-1">Possible match found</div>
        <h2 className="font-heading uppercase text-xl">Same phone number</h2>
        <p className="text-sm text-zinc-400 mt-2" data-testid="phone-match-text">
          The phone number you entered matches <span className="text-white font-semibold">{data.match.name}</span>
          {data.match.email ? <span className="text-zinc-300"> ({data.match.email})</span> : null}, who already has a parent
          account. Link <span className="text-white font-semibold">{data.player_name}</span> to that account instead of
          sending a new invite?
        </p>
        {err && <div className="text-xs text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-2 mt-3">{err}</div>}
        <div className="grid gap-2 mt-5">
          <button onClick={linkIt} disabled={!!busy} className="btn-primary w-full" data-testid="phone-match-link">
            {busy === "link" ? "Linking…" : `Yes — link to ${data.match.name}`}
          </button>
          <button onClick={separateInvite} disabled={!!busy} className="btn-ghost w-full" data-testid="phone-match-invite">
            {busy === "invite" ? "Creating invite…" : "No — create a separate invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ invite, onClose }) {
  const [copied, setCopied] = useState(false);
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [emailErr, setEmailErr] = useState("");

  const sendEmail = async () => {
    if (!invite.parent_email || emailState === "sending") return;
    setEmailState("sending");
    setEmailErr("");
    try {
      await api.post(`/invites/${invite.id}/send-email`, { base_url: window.location.origin });
      setEmailState("sent");
    } catch (e) {
      setEmailState("error");
      setEmailErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const url = `${window.location.origin}/invite/${invite.token}`;
  const message =
    `Hi ${invite.parent_name}, here's your invite to track ${invite.player_name}'s tennis progress on courtsideOS: ${url}`;
  const phone = normalisePhone(invite.parent_phone);
  // Use api.whatsapp.com/send — more reliable across devices than wa.me, especially the
  // no-recipient share flow which often triggers "Check this link" errors in mobile browsers.
  const waUrl = phone
    ? `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label">Invite ready</div>
            <h2 className="font-heading uppercase text-xl">Share with {invite.parent_name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        <div className="bg-[#09090b] border border-[#27272a] rounded-md p-3 mb-4">
          <div className="font-label mb-1">Activation link</div>
          <div className="text-xs text-zinc-300 break-all font-mono" data-testid="invite-link">{url}</div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="surface p-4 flex flex-col items-center gap-2 hover:border-[#16ec06]/60 transition-colors text-[#16ec06]"
            data-testid="share-whatsapp"
            title={phone ? `Will message +${phone}` : "Will open WhatsApp share picker"}
          >
            <WhatsappLogo size={28} weight="fill" />
            <span className="text-xs font-bold uppercase tracking-wider">WhatsApp</span>
            <span className="text-[10px] text-zinc-500 font-mono">{phone ? `+${phone}` : "share"}</span>
          </a>
          <button
            type="button"
            onClick={sendEmail}
            disabled={!invite.parent_email || emailState === "sending"}
            className={`surface p-4 flex flex-col items-center gap-2 transition-colors ${invite.parent_email ? "hover:border-[#dfff00]/60 text-[#dfff00]" : "opacity-40 cursor-not-allowed text-zinc-500"}`}
            data-testid="share-email"
            title={invite.parent_email ? `Send to ${invite.parent_email}` : "No email on this invite"}
          >
            {emailState === "sent" ? <Check size={28} weight="bold" className="text-[#16ec06]" /> : <EnvelopeSimple size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">
              {emailState === "sending" ? "Sending…" : emailState === "sent" ? "Sent" : "Email"}
            </span>
            {invite.parent_email && (
              <span className="text-[10px] text-zinc-500 font-mono truncate max-w-full">{invite.parent_email}</span>
            )}
          </button>
          <button
            onClick={copy}
            className="surface p-4 flex flex-col items-center gap-2 hover:border-[#e3735e]/60 transition-colors text-[#e3735e]"
            data-testid="share-copy"
          >
            {copied ? <Check size={28} weight="bold" /> : <Copy size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>

        {emailState === "error" && (
          <div className="text-xs text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-2 mt-3" data-testid="share-email-error">
            {emailErr}
          </div>
        )}

        <p className="text-xs text-zinc-500 mt-4">
          Parent clicks the link, sets a password, and is auto-linked to {invite.player_name}.
          Link is single-use and can be regenerated.
        </p>
      </div>
    </div>
  );
}
